import { ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GadgetComplete } from 'node_js/models/business/lab/gadget-complete';
import { RoleEnum } from 'node_js/models/enums/role';
import { MainSliderSection } from 'src/app/pages/dashboard/dashboard.page';
import { LabService } from 'src/app/services/lab.service';
import { CreateGadgetComponentComponent } from '../create-gadget-component/create-gadget-component.component';
import { ComponentComplete } from 'node_js/models/business/lab/component-complete';
import { animate, style, transition, trigger } from '@angular/animations';
import { GadgetSettingsComponent } from '../gadget-settings/gadget-settings.component';
import { GadgetUserListComponent } from '../gadget-user-list/gadget-user-list.component';
import { TutorialService } from 'src/app/services/tutorial.service';
import { LanguageService } from 'src/app/services/language.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarColor } from 'node_js/models/enums/snackbar-color';
import { convertUTCStringToLocalDate, presentAlertConfirm, presentLoading, presentSnackBar } from 'src/global';
import { GadgetService } from 'src/app/services/gadget.service';
import { UserAuthService } from 'src/app/services/user-auth.service';

enum SelectedGadgetSection {
  COMPONENTS = "components",
  MEMBERS = "members",
  SETTINGS = "settings"
}

@Component({
  selector: 'gadget',
  templateUrl: './gadget.component.html',
  styleUrls: ['./gadget.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateY(80%)' }),
        animate('100ms ease-in', style({ transform: 'translateY(0%)' }))
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({ transform: 'translateY(80%)', height: '0px' }))
      ])
    ])
  ]
})
export class GadgetComponent implements DoCheck {

  RoleEnum = RoleEnum;
  SelectedGadgetSection = SelectedGadgetSection;
  MainSliderSection = MainSliderSection;
  @Input() initialRender: boolean = false;
  @Output() changeMainSlide: EventEmitter<MainSliderSection> = new EventEmitter();

  displayGadgetSettings = false
  selectedGadgetSection: SelectedGadgetSection = SelectedGadgetSection.COMPONENTS;
  gadgetComplete: GadgetComplete;

  constructor(protected labService: LabService, private dialog: MatDialog, private changeDetectorRef: ChangeDetectorRef,
    public tutorialService: TutorialService, private languageService: LanguageService,
    private gadgetService: GadgetService, private userAuthService: UserAuthService,
    private matSnackBar: MatSnackBar) {

  }

  ngDoCheck() {
    if (this.tutorialService.isCreateComponentTutorialActive && this.selectedGadgetSection !== SelectedGadgetSection.COMPONENTS) {
      this.selectedGadgetSection = SelectedGadgetSection.COMPONENTS;
    }
  }

  // Simple initialization without aggressive preloading
  ngOnInit() {
    // Component is created but data might not be ready yet
    // This is normal when using [hidden] instead of *ngIf
    console.log('Gadget component initialized, gadgetComplete:', this.gadgetComplete);
    console.log('Lab service currentSelectedGadget:', this.labService.currentSelectedGadget);

    // If data is already available, initialize the component
    if (this.gadgetComplete) {
      this.resetComponent();
    }

    // Also check if service has data and sync if needed
    if (this.labService.currentSelectedGadget && !this.gadgetComplete) {
      this.gadgetComplete = this.labService.currentSelectedGadget;
      this.resetComponent();
    }

    // Set up a watcher for when the gadget service data changes
    this.watchGadgetServiceChanges();

    // Refresh gadgetComplete when gadget settings are updated so the header re-renders
    this.labService.currentSelectedGadgetSettingsChanged.subscribe(() => {
      if (this.labService.currentSelectedGadget) {
        this.gadgetComplete = this.labService.currentSelectedGadget;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private watchGadgetServiceChanges() {
    // Since the component is always loaded, we need to watch for changes in the service
    // Check every 500ms if the gadget data has changed (less aggressive)
    setInterval(() => {
      try {
        if (this.labService.currentSelectedGadget &&
          (!this.gadgetComplete || this.gadgetComplete.gadget?.id !== this.labService.currentSelectedGadget.gadget?.id)) {
          console.log('Gadget service data changed, updating component');
          this.gadgetComplete = this.labService.currentSelectedGadget;
          this.resetComponent();
        }
      } catch (error) {
        console.warn('Error in gadget service watcher:', error);
      }
    }, 500);
  }

  // Simple data preloading without complex image handling
  private preloadAllImagesAggressively() {
    // Removed complex preloading
  }



  resetComponent() {
    // Defer heavy operations to improve slide transition performance
    setTimeout(() => {
      this.gadgetComplete = this.labService.currentSelectedGadget;
      // Use optimized change detection
      this.changeDetectorRef.markForCheck();
    }, 50);
  }

  // Preload data before slide transition for better performance
  preloadData() {
    try {
      // Start loading data in background before transition
      this.gadgetComplete = this.labService.currentSelectedGadget;

      // Minimal image preloading - just start loading in background
      if (this.gadgetComplete && this.gadgetComplete.componentsComplete) {
        this.gadgetComplete.componentsComplete.forEach(component => {
          if (component.iconFileUploadComplete?.srcUrl) {
            // Start preloading without waiting
            this.preloadImage(component.iconFileUploadComplete.srcUrl);
          }
        });
      }

      console.log('Gadget data preloaded');
    } catch (error) {
      console.warn('Error preloading gadget data:', error);
    }
  }

  // Preload data and wait for all images to complete
  async preloadDataWithImages(): Promise<void> {
    try {
      // Start loading data in background before transition
      this.gadgetComplete = this.labService.currentSelectedGadget;

      // Simple data preloading without complex image handling
      console.log('Gadget data preloaded with images');
    } catch (error) {
      console.warn('Error preloading gadget data with images:', error);
    }
  }

  // Simple data preloading without complex image handling
  private preloadAllComponentImages() {
    // Removed complex image preloading
  }

  // Simple data preloading without complex image handling
  private async preloadAllComponentImagesWithWait(): Promise<void> {
    // Removed complex image preloading
  }

  // Simple image preloading
  private preloadImage(src: string) {
    if (src && !this.isImagePreloaded(src)) {
      const img = new Image();
      img.src = src;
      this.preloadedImages.add(src);
    }
  }

  // Simple image preloading with promise
  private preloadImageImmediately(src: string): Promise<void> {
    return new Promise((resolve) => {
      if (!src) {
        resolve();
        return;
      }

      const img = new Image();
      img.onload = () => {
        this.preloadedImages.add(src);
        resolve();
      };
      img.onerror = () => {
        resolve();
      };
      img.src = src;
    });
  }

  // Simple image preloading without complex DOM manipulation
  private forceImageRender(src: string) {
    // Removed complex DOM manipulation
  }

  // Simple image cache check
  private isImageFullyLoaded(src: string): boolean {
    return this.preloadedImages.has(src);
  }

  // Track preloaded images
  private preloadedImages = new Set<string>();

  private isImagePreloaded(src: string): boolean {
    return this.preloadedImages.has(src);
  }

  toggleGadgetSettings() {
    this.displayGadgetSettings = !this.displayGadgetSettings;
  }

  test() {
    console.log("detecting changes")
    this.changeDetectorRef.detectChanges();
  }

  changeSelectedGadgetSection(selectedGadgetSection: SelectedGadgetSection) {
    this.selectedGadgetSection = selectedGadgetSection;
  }

  startCreateComponentTutorial() {
    this.tutorialService.startCreateComponentTutorial();
  }

  loadCreateGadgetComponentDialog() {
    const isTutorialCreateFlow = this.tutorialService.isStepActive('gadget-components-start');

    if (isTutorialCreateFlow) {
      this.tutorialService.openCreateComponentTutorialStep();
    }

    let dialogRef = this.dialog.open(CreateGadgetComponentComponent, {
      height: '600px',
      width: '370px',
      maxWidth: "93vw",
      data: {},
      autoFocus: false,

    });

    // Handle dialog closure to re-establish dashboard back button handler
    dialogRef.afterClosed().subscribe((componentComplete: ComponentComplete) => {
      if (componentComplete) {
        setTimeout(() => this.loadComponent(componentComplete), 200);
        return;
      }

      if (isTutorialCreateFlow) {
        this.tutorialService.noteCreateComponentDialogClosed(false);
      }
    });

    // Also handle beforeClose to ensure proper cleanup
    dialogRef.beforeClosed().subscribe(() => {
      console.log('[Gadget] CreateGadgetComponent dialog beforeClose, ensuring cleanup...');
      // Get the component instance and cleanup the back button subscription
      const componentInstance = dialogRef.componentInstance;
      if (componentInstance && typeof componentInstance.cleanupBeforeClose === 'function') {
        componentInstance.cleanupBeforeClose();
      }
    });
  }

  loadGadgetSettingsComponentDialog() {
    let dialogRef = this.dialog.open(GadgetSettingsComponent, {
      height: '600px',
      width: '370px',
      maxWidth: "93vw",
      data: {},
      autoFocus: false,
    });

    // Also handle beforeClose to ensure proper cleanup
    dialogRef.beforeClosed().subscribe(() => {
      console.log('[Gadget] GadgetSettings dialog beforeClose, ensuring cleanup...');
      // Get the component instance and cleanup the back button subscription
      const componentInstance = dialogRef.componentInstance;
      if (componentInstance && typeof componentInstance.cleanupBeforeClose === 'function') {
        componentInstance.cleanupBeforeClose();
      }
    });
  }

  loadGadgetUserListComponentDialog() {
    let data = {
      labComplete: this.labService.currentSelectedLab,
      gadgetComplete: this.gadgetComplete
    }
    let dialogRef = this.dialog.open(GadgetUserListComponent, {
      height: 'auto',
      maxHeight: '600px',
      width: '370px',
      maxWidth: "93vw",
      data,
      autoFocus: false,

    });

    // Also handle beforeClose to ensure proper cleanup
    dialogRef.beforeClosed().subscribe(() => {
      console.log('[Gadget] GadgetUserList dialog beforeClose, ensuring cleanup...');
      // Get the component instance and cleanup the back button subscription
      const componentInstance = dialogRef.componentInstance;
      if (componentInstance && typeof componentInstance.cleanupBeforeClose === 'function') {
        componentInstance.cleanupBeforeClose();
      }
    });
  }

  formatComponentLastActive(dateValue?: string): string {
    if (!dateValue) {
      return this.languageService.translateUiLiteral('No updates yet');
    }

    const date = convertUTCStringToLocalDate(dateValue);
    if (isNaN(date.getTime())) {
      return this.languageService.translateUiLiteral('No updates yet');
    }

    const diffMs = date.getTime() - Date.now();
    const diffSeconds = Math.round(diffMs / 1000);
    const absoluteSeconds = Math.abs(diffSeconds);
    const rtf = new Intl.RelativeTimeFormat(this.languageService.activeLanguage?.abbreviation || 'en', { numeric: 'auto' });

    if (absoluteSeconds < 60) {
      return `${this.languageService.translateUiLiteral('Updated')} ${rtf.format(Math.round(diffSeconds), 'second')}`;
    }

    const diffMinutes = Math.round(diffSeconds / 60);
    if (Math.abs(diffMinutes) < 60) {
      return `${this.languageService.translateUiLiteral('Updated')} ${rtf.format(diffMinutes, 'minute')}`;
    }

    const diffHours = Math.round(diffMinutes / 60);
    if (Math.abs(diffHours) < 24) {
      return `${this.languageService.translateUiLiteral('Updated')} ${rtf.format(diffHours, 'hour')}`;
    }

    const diffDays = Math.round(diffHours / 24);
    return `${this.languageService.translateUiLiteral('Updated')} ${rtf.format(diffDays, 'day')}`;
  }

  formatAbsoluteDate(dateValue?: string): string {
    if (!dateValue) return '';
    const date = convertUTCStringToLocalDate(dateValue);
    return isNaN(date.getTime()) ? '' : date.toLocaleString();
  }

  loadComponent(componentComplete: ComponentComplete) {
    this.labService.currentSelectedComponent = componentComplete;
    this.changeMainSlide.emit(MainSliderSection.COMPONENT);
  }

  deleteComponent(event: Event, componentComplete: ComponentComplete) {
    event.stopPropagation();
    presentAlertConfirm("Delete Component", "Are you sure you want to delete this component?", this.dialog).then((result) => {
      if (result) {
        let loadingRef = presentLoading(this.dialog);
        this.gadgetService.deleteComponent(this.userAuthService, componentComplete.component.gadgetId, componentComplete.component.id).then(() => {
          presentSnackBar("Component has been deleted!", SnackBarColor.SUCCESS, this.matSnackBar);
          this.gadgetService.deleteComponentLocally(componentComplete.component.id, this.userAuthService);
          this.resetComponent();
        }).finally(() => {
          loadingRef.close();
        });
      }
    });
  }
}
